import { MagnifyingGlassIcon, StarIcon as SolidStarIcon } from '@heroicons/react/20/solid'
import { StarIcon as OutlineStarIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {
    type KeyboardEvent,
    type MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useRecoilValue } from 'recoil'
import { AccountRecord, useSearchAccountsQuery } from '../../api/client'
import { canChangeCrossProfileEngageDefaultState } from '../../store/DomainStore'
import { toastError } from '../Notification'
import { getAccountDisplayName } from '../../util/account-utils'
import CrossProfileEngageAccountRow from './CrossProfileEngageAccountRow'
import CrossProfileEngageUnlikeView from './CrossProfileEngageUnlikeView'

type Props = {
    currentAccount: AccountRecord
    onAccountSelect: (account: AccountRecord) => void
    onMakeDefault?: (account: AccountRecord) => void
    selectedAccountId?: number
    defaultAccountId?: number
    likedAccountId?: number
    likeApplied?: boolean
    onRequestClose?: () => void
    mode?: 'like' | 'reply'
}

/** Renders the popover content for Cross Profile Engage, including filtering, selection and default controls. */
export default function CrossProfileEngageSelectionComponent(props: Props) {
    const {
        currentAccount,
        onAccountSelect,
        onMakeDefault,
        defaultAccountId,
        likedAccountId,
        likeApplied,
        onRequestClose,
        selectedAccountId,
        mode = 'like'
    } = props

    const canEditDefault = useRecoilValue(canChangeCrossProfileEngageDefaultState)

    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        setSearchTerm('')
    }, [currentAccount.id])

    useEffect(() => {
        if (mode === 'like' && likeApplied) {
            setSearchTerm('')
        }
    }, [likeApplied, mode])

    const socialMediaService = currentAccount.socialMediaServiceShortName || ''
    const trimmedSearchTerm = searchTerm.trim()

    const { data, loading } = useSearchAccountsQuery({
        variables: {
            params: {
                limit: 250,
                offset: 0,
                teamIds:
                    currentAccount.teamId === null || currentAccount.teamId === undefined
                        ? []
                        : [currentAccount.teamId],
                socialMediaServices: socialMediaService ? [socialMediaService] : [],
                ...(trimmedSearchTerm
                    ? {
                          keyword: trimmedSearchTerm
                      }
                    : {})
            }
        },
        onError: (error) => toastError(error.message),
        fetchPolicy: 'cache-first'
    })

    // Deduplicate accounts from the current selection and search results, then filter by
    // team/service and finally pin the default/current accounts before sorting everything else.
    const candidateAccounts = useMemo(() => {
        const accountSearchResults = data?.searchAccounts?.accounts || []

        // A Hashmap is used to ensure uniqueness and the process also removes any null/undefined
        const map = new Map<number, AccountRecord>()
        if (currentAccount?.id) {
            map.set(currentAccount.id, currentAccount)
        }

        accountSearchResults.forEach((account) => {
            if (account?.id) {
                map.set(account.id, account)
            }
        })

        // Filtering...
        const results = Array.from(map.values()).filter((account) => {
            const matchesTeam =
                currentAccount.teamId === null || currentAccount.teamId === undefined
                    ? account.teamId === null || account.teamId === undefined
                    : account.teamId === currentAccount.teamId

            if (!matchesTeam) {
                return false
            }

            if (!socialMediaService) {
                return true
            }

            return (
                account.socialMediaServiceShortName?.toLowerCase() ===
                socialMediaService.toLowerCase()
            )
        })

        // Sort by priority: selected account, default account, current account, then anything else.
        const sortedAccounts = results.sort((a, b) => {
            const priority = (account: AccountRecord) => {
                if (selectedAccountId !== undefined && account.id === selectedAccountId) {
                    return -1
                }
                if (defaultAccountId !== undefined && account.id === defaultAccountId) {
                    return 0
                }
                if (account.id === currentAccount.id) {
                    return 1
                }
                if (mode === 'like' && likeApplied && likedAccountId === account.id) {
                    return 2
                }
                return 3
            }

            const aPriority = priority(a)
            const bPriority = priority(b)

            if (aPriority !== bPriority) {
                return aPriority - bPriority
            }

            const aName = getAccountDisplayName(a).toLowerCase()
            const bName = getAccountDisplayName(b).toLowerCase()
            return aName.localeCompare(bName)
        })

        const restrictToLikedAccount =
            mode === 'like' &&
            likeApplied &&
            likedAccountId !== undefined &&
            likedAccountId !== null

        if (restrictToLikedAccount) {
            const likedAccountFromResults =
                sortedAccounts.find((account) => account.id === likedAccountId) ||
                (currentAccount.id === likedAccountId ? currentAccount : undefined)
            console.info(likedAccountId, likedAccountFromResults)
            if (likedAccountFromResults) {
                return [likedAccountFromResults]
            }
        }

        return sortedAccounts
    }, [
        currentAccount,
        data?.searchAccounts,
        defaultAccountId,
        likeApplied,
        likedAccountId,
        mode,
        selectedAccountId,
        socialMediaService
    ])

    const likedAccount = useMemo(() => {
        if (mode !== 'like') {
            return undefined
        }

        if (!likeApplied || likedAccountId === undefined || likedAccountId === null) {
            return undefined
        }

        return (
            candidateAccounts.find((account) => account.id === likedAccountId) ||
            (currentAccount.id === likedAccountId ? currentAccount : undefined)
        )
    }, [candidateAccounts, currentAccount, likeApplied, likedAccountId, mode])

    const isUnlikeFlow =
        mode === 'like' &&
        Boolean(likeApplied && likedAccountId !== undefined && likedAccountId !== null)

    const handleAccountKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>, account: AccountRecord, disabled: boolean) => {
            if (event.key === 'Escape') {
                // Give keyboard users the same escape hatch they get with the mouse: hitting
                // Escape should dismiss the popover without performing any action.
                event.preventDefault()
                onRequestClose?.()
                return
            }

            if (disabled) {
                return
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onAccountSelect(account)
                onRequestClose?.()
                return
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                // The list is intentionally navigable with Arrow keys so it behaves like a
                // composite button list: arrows move focus, Enter/Space commit the action. We do
                // not call `onAccountSelect` here because selection triggers the like/unlike side
                // effect immediately.
                event.preventDefault()

                const currentRow = event.currentTarget.closest('li')
                if (!currentRow) {
                    return
                }

                const moveForward = event.key === 'ArrowDown'
                let sibling: Element | null = moveForward
                    ? currentRow.nextElementSibling
                    : currentRow.previousElementSibling

                while (sibling) {
                    const button = sibling.querySelector<HTMLDivElement>(
                        'div[role="button"]:not([aria-disabled="true"])'
                    )
                    if (button) {
                        button.focus({ preventScroll: true })
                        return
                    }

                    sibling = moveForward
                        ? sibling.nextElementSibling
                        : sibling.previousElementSibling
                }
            }
        },
        [onAccountSelect, onRequestClose]
    )

    const handleAccountClick = useCallback(
        (account: AccountRecord, disabled: boolean) => {
            if (disabled) {
                return
            }

            onAccountSelect(account)
            // Close the popover so the user gets instant feedback after choosing a profile.
            onRequestClose?.()
        },
        [onAccountSelect, onRequestClose]
    )

    const handleMakeDefault = useCallback(
        (event: MouseEvent<HTMLButtonElement>, account: AccountRecord) => {
            event.stopPropagation()
            event.preventDefault()

            onMakeDefault?.(account)
        },
        [onMakeDefault]
    )

    const popoverTitle = mode === 'reply' ? 'Reply as' : 'Engage as'

    const instructionCopy = useMemo(() => {
        if (mode === 'reply') {
            return 'Select a profile below to reply using that account. Use "Make default" to change your default reply profile without sending a response.'
        }

        return 'Click a profile below to like this comment. Selecting a profile also makes it your default for next time. Use "Make default" to change your default profile without applying a like.'
    }, [mode])

    if (isUnlikeFlow) {
        return (
            <CrossProfileEngageUnlikeView
                accounts={candidateAccounts}
                currentAccount={currentAccount}
                likedAccount={likedAccount}
                selectedAccountId={selectedAccountId}
                loading={loading}
                onAccountClick={handleAccountClick}
                onAccountKeyDown={handleAccountKeyDown}
            />
        )
    }

    const canEditDefaultForView = canEditDefault

    return (
        <div className="space-y-4 text-gray-800">
            <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-950">{popoverTitle}</p>
                <p className="text-xs text-gray-500">{instructionCopy}</p>
            </div>

            <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute inset-y-0 left-3 h-4 w-4 self-center text-gray-500" />
                <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search profiles"
                    className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    aria-label="Search profiles"
                />
            </div>

            {loading ? (
                <div className="py-6 text-center text-sm text-gray-700">
                    Loading available profiles…
                </div>
            ) : (
                <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
                    {candidateAccounts.map((account) => {
                        const isSelectedAccount = selectedAccountId === account.id
                        const isLikedAccount =
                            mode === 'like' && likeApplied && likedAccount?.id === account.id
                        const isDefaultAccount = defaultAccountId === account.id
                        const isDisabled = !account.hasAuthorisation

                        const defaultControl = isDefaultAccount ? (
                            <span
                                key="default"
                                className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
                            >
                                <SolidStarIcon className="h-4 w-4" aria-hidden="true" />
                                Default
                            </span>
                        ) : canEditDefaultForView ? (
                            <button
                                key="make-default"
                                type="button"
                                onClick={(event) => handleMakeDefault(event, account)}
                                disabled={isDisabled}
                                className={clsx(
                                    'inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-1 text-xs font-medium transition',
                                    isDisabled
                                        ? 'cursor-not-allowed text-gray-400'
                                        : 'text-gray-500 hover:border-primary-200 hover:text-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-300'
                                )}
                            >
                                <OutlineStarIcon className="h-4 w-4" aria-hidden="true" />
                                Make default
                            </button>
                        ) : null

                        return (
                            <CrossProfileEngageAccountRow
                                key={account.id}
                                account={account}
                                currentAccountId={currentAccount.id}
                                isSelected={isSelectedAccount}
                                isHighlighted={isLikedAccount}
                                rightContent={defaultControl}
                                onAccountClick={handleAccountClick}
                                onAccountKeyDown={handleAccountKeyDown}
                            />
                        )
                    })}
                </ul>
            )}

            {!loading && candidateAccounts.length === 0 && (
                <p className="rounded-md bg-gray-200 px-3 py-2 text-xs text-gray-800">
                    We couldn’t find any profiles that match “{searchTerm}”. Adjust your search or
                    add a new profile for this network.
                </p>
            )}

            {!loading && candidateAccounts.length > 0 && (
                <p className="rounded-md bg-gray-200 px-3 py-2 text-xs text-gray-800">
                    Want more options? Add another authorised profile for this network.
                </p>
            )}
        </div>
    )
}
